export const PRO_LABORE_PERCENTAGE = 0.3;
export const INSS_PERCENTAGE = 0.11;
export const BASE_SAMPLE = 25224.22;

export const TAX_SAMPLE = Object.freeze([
  { code: '1001', name: 'IRPJ', sampleValue: 83.79 },
  { code: '1002', name: 'CSLL', sampleValue: 73.31 },
  { code: '1004', name: 'COFINS', sampleValue: 294.3 },
  { code: '1005', name: 'PIS', sampleValue: 63.89 },
  { code: '1006', name: 'INSS', sampleValue: 909.07 },
  { code: '1010', name: 'ISS', sampleValue: 670.28 },
]);

export const deriveTaxRates = (baseValue = BASE_SAMPLE, sample = TAX_SAMPLE) =>
  sample.map((tax) => ({
    ...tax,
    rate: baseValue > 0 ? tax.sampleValue / baseValue : 0,
  }));

export const TAX_RATES = deriveTaxRates();

const IRPF_TABLE = Object.freeze([
  { ceiling: 2259.2, rate: 0, deduction: 0 },
  { ceiling: 2826.65, rate: 0.075, deduction: 169.44 },
  { ceiling: 3751.05, rate: 0.15, deduction: 381.44 },
  { ceiling: 4664.68, rate: 0.225, deduction: 662.77 },
  { ceiling: Number.POSITIVE_INFINITY, rate: 0.275, deduction: 896 },
]);

export const calculateINSS = (proLaboreBase, rate = INSS_PERCENTAGE) => {
  if (!Number.isFinite(proLaboreBase) || proLaboreBase <= 0) {
    return 0;
  }
  return proLaboreBase * rate;
};

export const calculateIRPF = (proLaboreBase, inssDiscount) => {
  if (!Number.isFinite(proLaboreBase) || proLaboreBase <= 0) {
    return 0;
  }

  const baseCalculo = Math.max(0, proLaboreBase - (inssDiscount || 0));
  if (baseCalculo <= 0) {
    return 0;
  }

  const faixa = IRPF_TABLE.find((entry) => baseCalculo <= entry.ceiling);
  if (!faixa) {
    return 0;
  }

  const imposto = baseCalculo * faixa.rate - faixa.deduction;
  return Math.max(0, imposto);
};

export const calculateProLaboreBase = (notaValue, percentage = PRO_LABORE_PERCENTAGE) => {
  if (!Number.isFinite(notaValue) || notaValue <= 0) {
    return 0;
  }
  return notaValue * percentage;
};

export const calculateNotaFiscalTaxes = (notaValue, taxes = TAX_RATES) =>
  taxes.map((tax) => ({
    code: tax.code,
    name: tax.name,
    baseLabel: 'Nota fiscal',
    rate: tax.rate,
    value: notaValue > 0 ? notaValue * tax.rate : 0,
  }));

export const calculateProLaboreTaxes = (proLaboreBase) => {
  const inssValue = calculateINSS(proLaboreBase);
  const irpfValue = calculateIRPF(proLaboreBase, inssValue);

  const taxes = [
    {
      code: 'INSS-PRL',
      name: 'INSS (Pró-labore)',
      baseLabel: 'Pró-labore (30% da nota)',
      rate: proLaboreBase > 0 ? inssValue / proLaboreBase : 0,
      value: inssValue,
    },
    {
      code: 'IRPF',
      name: 'IRPF',
      baseLabel: 'Pró-labore (30% da nota)',
      rate: proLaboreBase > 0 ? irpfValue / proLaboreBase : 0,
      value: irpfValue,
    },
  ];

  return {
    taxes,
    totals: {
      proLaboreBase,
      inssValue,
      irpfValue,
    },
  };
};

export const computeTaxBreakdown = (notaValue, taxRates = TAX_RATES) => {
  if (!Number.isFinite(notaValue) || notaValue <= 0) {
    return {
      notaFiscalTaxes: [],
      proLaboreTaxes: [],
      totals: {
        totalNotaFiscal: 0,
        totalProLabore: 0,
        totalImpostos: 0,
        valorLiquido: 0,
        proLaboreBase: 0,
      },
    };
  }

  const notaFiscalTaxes = calculateNotaFiscalTaxes(notaValue, taxRates);
  const proLaboreBase = calculateProLaboreBase(notaValue);
  const { taxes: proLaboreTaxes } = calculateProLaboreTaxes(proLaboreBase);

  const totalNotaFiscal = notaFiscalTaxes.reduce((acc, tax) => acc + tax.value, 0);
  const totalProLabore = proLaboreTaxes.reduce((acc, tax) => acc + tax.value, 0);
  const totalImpostos = totalNotaFiscal + totalProLabore;
  const valorLiquido = notaValue - totalNotaFiscal;

  return {
    notaFiscalTaxes,
    proLaboreTaxes,
    totals: {
      totalNotaFiscal,
      totalProLabore,
      totalImpostos,
      valorLiquido,
      proLaboreBase,
    },
  };
};

const taxesModule = {
  PRO_LABORE_PERCENTAGE,
  INSS_PERCENTAGE,
  BASE_SAMPLE,
  TAX_SAMPLE,
  TAX_RATES,
  calculateNotaFiscalTaxes,
  calculateProLaboreTaxes,
  calculateProLaboreBase,
  calculateINSS,
  calculateIRPF,
  computeTaxBreakdown,
};

export default taxesModule;
